import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { API } from 'aws-amplify';
import { toast } from 'react-hot-toast';
import {
  ArrowLeftIcon,
  MapPinIcon,
  UserGroupIcon,
  StarIcon,
  CheckBadgeIcon,
  TagIcon,
  BriefcaseIcon,
  ChartBarIcon,
  PhotoIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { PublicInfluencer } from '../../types';
import { Spinner } from '../common/Spinner';
import { SkeletonDetail } from '../common/SkeletonDetail';
import { GET_PUBLIC_INFLUENCER } from '../../graphql/queries';
import { useAuthStore } from '../../store/authStore';
import { withRetry, handleApiError, ApiError } from '../../utils/apiErrorHandler';
import GlobalNavBar from '../layout/GlobalNavBar';
import SiteFooter from '../layout/SiteFooter';

export const PublicInfluencerProfile: React.FC = () => {
  const { influencerId } = useParams<{ influencerId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [influencer, setInfluencer] = useState<PublicInfluencer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (influencerId) {
      fetchInfluencerProfile();
    }
  }, [influencerId]);

  const fetchInfluencerProfile = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await withRetry(
        () => API.graphql({
          query: GET_PUBLIC_INFLUENCER,
          variables: { influencerId },
          authMode: 'AWS_IAM',
        }) as Promise<any>,
        { maxRetries: 2 }
      );

      const influencerData = response.data?.getPublicInfluencer;

      if (!influencerData) {
        const notFoundError: ApiError = {
          statusCode: 404,
          message: 'Influencer profile not found',
          code: 'NOT_FOUND',
        };
        setError(notFoundError);
        toast.error('Influencer profile not found');
      } else {
        setInfluencer(influencerData);
        setRetryCount(0);
      }
    } catch (err: any) {
      console.error('Error fetching influencer profile:', err);
      const apiError = handleApiError(err, 'Failed to load influencer profile');
      setError(apiError);
      setRetryCount(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  const handleContact = () => {
    if (isAuthenticated) {
      // If authenticated, navigate to messaging
      navigate(`/messages?influencerId=${influencerId}`);
    } else {
      // If not authenticated, redirect to register with return path
      navigate('/register', { state: { returnTo: `/influencers/${influencerId}` } });
    }
  };

  const handleBackToDashboard = () => {
    navigate('/');
  };

  const getTotalFollowers = () => {
    if (!influencer) return 0;
    const counts = influencer.followerCounts;
    return Object.values(counts).reduce((total, count) => total + (count || 0), 0);
  };

  const formatFollowerCount = (count: number) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <StarIcon key={i} className="h-5 w-5 text-yellow-400 fill-current" />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <StarIcon key="half" className="h-5 w-5 text-yellow-400 fill-current opacity-50" />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <StarIcon key={`empty-${i}`} className="h-5 w-5 text-gray-300" />
      );
    }

    return stars;
  };

  if (loading) {
    return <SkeletonDetail variant="influencer" />;
  }

  if (error || !influencer) {
    const isNotFound = error?.statusCode === 404;
    const isRateLimited = error?.statusCode === 429;
    const isServiceUnavailable = error?.statusCode === 503;

    return (
      <div className="min-h-screen bg-gray-50">
        <GlobalNavBar variant="dark" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className={`rounded-lg shadow-sm border p-8 text-center ${
            isNotFound ? 'bg-blue-50 border-blue-200' :
            isRateLimited ? 'bg-yellow-50 border-yellow-200' :
            isServiceUnavailable ? 'bg-orange-50 border-orange-200' :
            'bg-red-50 border-red-200'
          }`}>
            <div className="flex justify-center mb-4">
              {isNotFound ? (
                <svg className="w-16 h-16 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              ) : (
                <ExclamationTriangleIcon className={`h-16 w-16 ${
                  isRateLimited ? 'text-yellow-500' :
                  isServiceUnavailable ? 'text-orange-500' :
                  'text-red-500'
                }`} />
              )}
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {isNotFound ? 'Influencer Not Found' :
               isRateLimited ? 'Too Many Requests' :
               isServiceUnavailable ? 'Service Temporarily Unavailable' :
               'Unable to Load Profile'}
            </h2>
            
            <p className={`mb-6 ${
              isNotFound ? 'text-blue-800' :
              isRateLimited ? 'text-yellow-800' :
              isServiceUnavailable ? 'text-orange-800' :
              'text-red-800'
            }`}>
              {error?.message || 'This influencer profile could not be found.'}
            </p>

            {isRateLimited && error?.retryAfter && (
              <p className="text-sm text-yellow-700 mb-4">
                Please wait {error.retryAfter} seconds before trying again.
              </p>
            )}

            <div className="flex gap-3 justify-center">
              {!isNotFound && (
                <button
                  onClick={fetchInfluencerProfile}
                  disabled={isRateLimited && !!error?.retryAfter}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                    isRateLimited && error?.retryAfter
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : isRateLimited
                      ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                      : isServiceUnavailable
                      ? 'bg-orange-600 text-white hover:bg-orange-700'
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  {retryCount > 0 ? `Try Again (Attempt ${retryCount + 1})` : 'Try Again'}
                </button>
              )}
              
              <button
                onClick={handleBackToDashboard}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors"
              >
                Back to Dashboard
              </button>
            </div>

            {retryCount > 2 && !isNotFound && (
              <div className="mt-6 p-4 bg-white rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>Still having trouble?</strong>
                </p>
                <p className="text-xs text-gray-600">
                  This profile may have been removed or is temporarily unavailable.
                  Try browsing other influencers or contact support if the problem persists.
                </p>
              </div>
            )}
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 animate-fadeIn">
      <GlobalNavBar variant="dark" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={handleBackToDashboard}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Dashboard
        </button>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-8">
            <div className="flex items-start space-x-6">
              <div className="relative flex-shrink-0">
                <img
                  src={influencer.profileImage || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(influencer.name || 'User') + '&background=6366f1&color=fff&size=128'}
                  alt={influencer.name}
                  className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
                />
                {influencer.isVerified && (
                  <CheckBadgeIcon className="absolute -bottom-2 -right-2 h-10 w-10 text-blue-500 fill-current bg-white rounded-full" />
                )}
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-white mb-2">
                  {influencer.name}
                </h1>
                <span className="inline-block px-4 py-1.5 bg-white bg-opacity-20 text-white text-sm font-semibold rounded-full mb-3">
                  {influencer.mainCategory}
                </span>
                <div className="flex items-center space-x-4 text-white">
                  <div className="flex items-center">
                    {renderStars(influencer.rating)}
                    <span className="ml-2 text-sm font-medium">{influencer.rating.toFixed(1)}</span>
                  </div>
                  <span className="text-sm">•</span>
                  <div className="flex items-center">
                    <ChartBarIcon className="h-5 w-5 mr-1" />
                    <span className="text-sm font-medium">{influencer.engagementRate}% engagement</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-8">
                {/* Bio */}
                <section>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">About</h2>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {influencer.bio}
                  </p>
                </section>

                {/* Categories */}
                {influencer.categories && influencer.categories.length > 0 && (
                  <section>
                    <h2 className="text-xl font-bold text-gray-900 mb-4">Content Categories</h2>
                    <div className="flex flex-wrap gap-2">
                      {influencer.categories.map((category, index) => (
                        <span
                          key={index}
                          className="px-4 py-2 bg-purple-100 text-purple-800 text-sm font-medium rounded-full"
                        >
                          {category}
                        </span>
                      ))}
                    </div>
                  </section>
                )}

                {/* Platform Metrics */}
                <section>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Platform Presence</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {influencer.followerCounts.instagram && (
                      <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-lg p-4 border border-pink-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Instagram</p>
                            <p className="text-2xl font-bold text-pink-600">
                              {formatFollowerCount(influencer.followerCounts.instagram)}
                            </p>
                            <p className="text-xs text-gray-500">followers</p>
                          </div>
                          <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center">
                            <PhotoIcon className="h-6 w-6 text-white" />
                          </div>
                        </div>
                      </div>
                    )}

                    {influencer.followerCounts.youtube && (
                      <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-lg p-4 border border-red-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600 mb-1">YouTube</p>
                            <p className="text-2xl font-bold text-red-600">
                              {formatFollowerCount(influencer.followerCounts.youtube)}
                            </p>
                            <p className="text-xs text-gray-500">subscribers</p>
                          </div>
                          <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center">
                            <PhotoIcon className="h-6 w-6 text-white" />
                          </div>
                        </div>
                      </div>
                    )}

                    {influencer.followerCounts.tiktok && (
                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600 mb-1">TikTok</p>
                            <p className="text-2xl font-bold text-gray-900">
                              {formatFollowerCount(influencer.followerCounts.tiktok)}
                            </p>
                            <p className="text-xs text-gray-500">followers</p>
                          </div>
                          <div className="w-12 h-12 bg-gradient-to-br from-gray-700 to-gray-900 rounded-full flex items-center justify-center">
                            <PhotoIcon className="h-6 w-6 text-white" />
                          </div>
                        </div>
                      </div>
                    )}

                    {influencer.followerCounts.twitter && (
                      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-4 border border-blue-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Twitter</p>
                            <p className="text-2xl font-bold text-blue-600">
                              {formatFollowerCount(influencer.followerCounts.twitter)}
                            </p>
                            <p className="text-xs text-gray-500">followers</p>
                          </div>
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                            <PhotoIcon className="h-6 w-6 text-white" />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </section>

                {/* Portfolio */}
                {influencer.portfolio && influencer.portfolio.length > 0 && (
                  <section>
                    <h2 className="text-xl font-bold text-gray-900 mb-4">Portfolio</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {influencer.portfolio.map((item, index) => (
                        <div
                          key={index}
                          className="aspect-square rounded-lg overflow-hidden bg-gray-100 hover:opacity-90 transition-opacity cursor-pointer"
                        >
                          <img
                            src={item}
                            alt={`Portfolio item ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Quick Stats Card */}
                <div className="bg-gray-50 rounded-lg p-6 sticky top-8">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Stats</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <MapPinIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-600">Location</p>
                        <p className="font-semibold text-gray-900">{influencer.location}</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <UserGroupIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-600">Total Reach</p>
                        <p className="font-semibold text-gray-900">
                          {formatFollowerCount(getTotalFollowers())} followers
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <ChartBarIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-600">Engagement Rate</p>
                        <p className="font-semibold text-gray-900">{influencer.engagementRate}%</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <StarIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-600">Rating</p>
                        <div className="flex items-center">
                          <p className="font-semibold text-gray-900 mr-2">{influencer.rating.toFixed(1)}</p>
                          <div className="flex">
                            {renderStars(influencer.rating)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {influencer.completedCampaigns !== undefined && influencer.completedCampaigns > 0 && (
                      <div className="flex items-start">
                        <BriefcaseIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm text-gray-600">Completed Campaigns</p>
                          <p className="font-semibold text-gray-900">{influencer.completedCampaigns}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Contact CTA */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <button
                      onClick={handleContact}
                      className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg hover:bg-purple-700 font-semibold transition-colors shadow-sm hover:shadow-md"
                    >
                      {isAuthenticated ? 'Contact Influencer' : 'Sign Up to Contact'}
                    </button>
                    
                    {!isAuthenticated && (
                      <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                        <p className="text-sm text-purple-900 font-medium mb-2">
                          Why create an account?
                        </p>
                        <ul className="text-xs text-purple-800 space-y-1">
                          <li className="flex items-start">
                            <CheckBadgeIcon className="h-4 w-4 mr-1 flex-shrink-0 mt-0.5" />
                            <span>Contact influencers directly</span>
                          </li>
                          <li className="flex items-start">
                            <CheckBadgeIcon className="h-4 w-4 mr-1 flex-shrink-0 mt-0.5" />
                            <span>Create and manage campaigns</span>
                          </li>
                          <li className="flex items-start">
                            <CheckBadgeIcon className="h-4 w-4 mr-1 flex-shrink-0 mt-0.5" />
                            <span>Track collaborations</span>
                          </li>
                          <li className="flex items-start">
                            <CheckBadgeIcon className="h-4 w-4 mr-1 flex-shrink-0 mt-0.5" />
                            <span>Access detailed analytics</span>
                          </li>
                        </ul>
                      </div>
                    )}

                    <p className="text-xs text-gray-500 text-center mt-4">
                      Already have an account?{' '}
                      <Link to="/login" className="text-purple-600 hover:text-purple-700 font-medium">
                        Sign in
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
};
